import Dexie, { Table } from 'dexie'
import { dbVersion } from './dbFunctions'
import {
  commonSchema,
  homeAndGardenSchema,
  imagesSchema,
  professionalSchema,
  universalSchema,
} from './dbSchema'
import {
  CommonSubTables,
  HomeSubTables,
  ImagesSubTables,
  ProfessionalSubTables,
  UniversalSubTables,
} from './dbTypes'

/**
 * Create the database with the given schema.
 * Follow the documentation of Dexie to learn more about the schema.
 * https://dexie.org/docs/Typescript
 */
class UniversalSub extends Dexie {
  dcaSettings!: Table<UniversalSubTables['IDcaSettings']>
  dcaFavFolder!: Table<UniversalSubTables['IDcaFavFolder']>
  dcaFavContent!: Table<UniversalSubTables['IDcaFavContent']>
  dcaProductCompare!: Table<UniversalSubTables['IDcaProductCompare']>
  dcaMarking!: Table<UniversalSubTables['IDcaMarking']>
  dcaOnboarding!: Table<UniversalSubTables['IDcaOnboarding']>

  constructor() {
    super('AK_DCA_LOCAL')
    this.version(dbVersion).stores(universalSchema)
  }

  async reset() {
    return new Promise<void>((resolve, reject) => {
      if (!this.isOpen()) {
        resolve()
        return
      }

      this.on('ready', async () => {
        try {
          await this.dcaSettings.clear()
          await this.dcaFavFolder.clear()
          await this.dcaFavContent.clear()
          await this.dcaProductCompare.clear()
          await this.dcaMarking.clear()
          await this.dcaOnboarding.clear()
          resolve()
        } catch (error) {
          console.error('Error reset AK_DCA_LOCAL', error)
          reject(error)
        }
      })
    })
  }

  async recreate() {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise<void>(async (resolve, reject) => {
      try {
        await Dexie.delete('AK_DCA_LOCAL')
        await this.open()
        resolve()
      } catch (error) {
        console.error('Error re-creating AK_DCA_LOCAL', error)
        reject(error)
      }
    })
  }
}

class CommonSub extends Dexie {
  dcaProductGroup!: Table<CommonSubTables['IDcaProductGroup']>
  dcaSubProductGroup!: Table<CommonSubTables['IDcaSubProductGroup']>
  dcaProductList!: Table<CommonSubTables['IDcaProductList']>
  dcaStructure!: Table<CommonSubTables['IDcaStructure']>
  dcaTranslations!: Table<CommonSubTables['IDcaTranslations']>
  dcaPages!: Table<CommonSubTables['IDcaPages']>

  constructor() {
    super('AK_DCA_COMMON')
    this.version(dbVersion).stores(commonSchema)
  }

  async reset() {
    return new Promise<void>((resolve, reject) => {
      if (!this.isOpen()) {
        resolve()
        return
      }

      this.on('ready', async () => {
        try {
          await this.dcaProductGroup.clear()
          await this.dcaSubProductGroup.clear()
          await this.dcaProductList.clear()
          await this.dcaStructure.clear()
          await this.dcaTranslations.clear()
          await this.dcaPages.clear()
          resolve()
        } catch (error) {
          console.error('Error reset AK_DCA_COMMON', error)
          reject(error)
        }
      })
    })
  }
}

class ImagesSub extends Dexie {
  dcaImages!: Table<ImagesSubTables['IDcaImages']>

  constructor() {
    super('AK_DCA_IMAGES')
    this.version(dbVersion).stores(imagesSchema)
  }

  async reset() {
    return new Promise<void>((resolve, reject) => {
      if (!this.isOpen()) {
        resolve()
        return
      }

      this.on('ready', async () => {
        try {
          await this.dcaImages.clear()
          resolve()
        } catch (error) {
          console.error('Error reset AK_DCA_IMAGES', error)
          reject(error)
        }
      })
    })
  }
}

class HomeSub extends Dexie {
  dcaProductDetails!: Table<HomeSubTables['IDcaProductDetails']>

  constructor() {
    super('AK_DCA_HOME_AND_GARDEN')
    this.version(dbVersion).stores(homeAndGardenSchema)
  }

  async reset() {
    return new Promise<void>((resolve, reject) => {
      if (!this.isOpen()) {
        resolve()
        return
      }

      this.on('ready', async () => {
        try {
          await this.dcaProductDetails.clear()
          resolve()
        } catch (error) {
          console.error('Error reset AK_DCA_HOME_AND_GARDEN', error)
          reject(error)
        }
      })
    })
  }
}

class ProfessionalSub extends Dexie {
  dcaProductDetails!: Table<ProfessionalSubTables['IDcaProductDetails']>

  constructor() {
    super('AK_DCA_PROFESSIONAL')
    this.version(dbVersion).stores(professionalSchema)
  }

  async reset() {
    return new Promise<void>((resolve, reject) => {
      if (!this.isOpen()) {
        resolve()
        return
      }

      this.on('ready', async () => {
        try {
          await this.dcaProductDetails.clear()
          resolve()
        } catch (error) {
          console.error('Error reset AK_DCA_PROFESSIONAL', error)
          reject(error)
        }
      })
    })
  }
}

export const universal = new UniversalSub()
export const common = new CommonSub()
export const imagesDb = new ImagesSub()
export const homeDb = new HomeSub()
export const professionalDb = new ProfessionalSub()
